import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import {Typography, Grid, Button, Chip, CircularProgress} from '@material-ui/core'

import _ from 'lodash'
import { Redirect } from 'react-router-dom'

import AssignmentIcon from '@material-ui/icons/Assignment.js';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {utc_timestamp_to_local_datetime_string} from "../../utils/datetime_utc_local";

import {ServerConfig} from 'config'
import {ALERT_FAIL} from '../../redux/actions/types.js'
import EditComponent from '../EntryListAndEdit/EditComponent.js'
import AddComponent from '../EntryListAndEdit/AddComponent.js'
import Select from "react-select";
import loading_gif from "../../assets/img/loading.gif";

const useStyles = makeStyles((theme) => ({
	serviceChip: {
		color: 'white',
		fontWeight: 'bold',
		textTransform: 'uppercase',
		marginRight: 4,
		marginBottom: 4,
		fontSize: '11px',
		height: 24,
		letterSpacing: '0.3px',
	},
	alt1: {
		backgroundColor: theme.palette.secondary.alt1,
	},
	alt2: {
		backgroundColor: theme.palette.secondary.alt2,
	},
	alt3: {
		backgroundColor: theme.palette.secondary.alt3,
	},
	alt4: {
		backgroundColor: theme.palette.secondary.alt4,
	},
	alt5: {
		backgroundColor: theme.palette.secondary.alt5,
	},
	table: {
		paddingTop: '0px',
		position: 'relative',
	},
	tableHeader: {
		position: 'sticky',
		top: 59,
		zIndex: 100,
		backgroundColor: theme.palette.background.default,
	},
	datePickerPopper: {
        zIndex: '999 !important',
    },
}));

const NonPaginatedList = (props) => {
	const classes = useStyles();

	const auth = useSelector((state) => state.auth);
	const token = useSelector((state) => state.auth.access_token);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [isDownloadingReceipts, setIsDownloadingReceipts] = useState(false);
	const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
	const [entries, setEntries] = useState([]);
	const [total, setTotal] = useState(0);
	const [searchterm, setSearchterm] = useState('');

	const [sortCol, setSortCol] = useState(null);
	const [sortDir, setSortDir] = useState('asc');

	const [availableMachines, setAvailableMachines] = useState([]);
	const [selectedMachine_ids, setSelectedMachine_ids] = useState([]);

	const listAbortControllerRef = useRef(null);

	const sortedEntries = React.useMemo(() => {
		if (!sortCol) return entries;
		return [...entries].sort((a, b) => {
			const av = a[sortCol] ?? '';
			const bv = b[sortCol] ?? '';
			const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
			return sortDir === 'asc' ? cmp : -cmp;
		});
	}, [entries, sortCol, sortDir]);

	// create state-dict for filters
	let initial_filter_values = {};
	for (let i in props.filter_inputs) {
		initial_filter_values[props.filter_inputs[i]['name']] = {
			type: props.filter_inputs[i]['type'],
			value: props.filter_inputs[i]['initial_value'],
		};
	}
	const [filterValues, setFilterValues] = useState(initial_filter_values);

	const [column_names, setColumnNames] = useState(props.column_names);
	const [header_names, setHeaderNames] = useState(props.header_names);

	/* check and update number of columns depending on screen-size */
	const [screenSize, setScreenSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	let list_get_url = props.list_get_url;
	let detail_get_url = props.detail_get_url;
	let detail_put_url = props.detail_put_url;
	let detail_delete_url = props.detail_delete_url;
	let ledger_get_url = props.ledger_get_url;
	let ledger_insert_url = props.ledger_insert_url;
	let add_new_url = props.add_new_url;
	let ledger_allow_entries = props.ledger_allow_entries;
	let show_machine_stats = props.show_machine_stats;

	let editable_fields = props.editable_fields;
	let non_editable_fields = props.non_editable_fields;

	function fetchMachineList(token) {
		const config = { headers: { 'Content-Type': 'application/json' } };

		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}

		axios.get(`${ServerConfig.SERVER_URL}/tenant/machines`, config).then((res) => {
			const options = [];

			for (let idx in res.data) {
				options.push({ value: res.data[idx].id, label: res.data[idx].name });
			}

			setAvailableMachines(options);
			setSelectedMachine_ids(options);
		});
	}

	useEffect(() => {
		fetchMachineList(token);
	}, []);

	// filterValues seeds machine_ids from a prop that may still be empty at mount time; default to "all machines" once the list is known
	useEffect(() => {
		if (availableMachines.length === 0) return;

		const machineFilter = props.filter_inputs?.find((f) => f['type'] === 'device_ids');
		if (!machineFilter) return;

		const current = filterValues[machineFilter['name']];
		if (current && Array.isArray(current.value) && current.value.length === 0) {
			updateFilterValues(
				machineFilter['type'],
				machineFilter['name'],
				availableMachines.map((m) => m.value),
			);
		}
	}, [availableMachines]);

	useEffect(() => {
		const fetchListData = () => {
			// cancel any request from this component that's still in flight
			if (listAbortControllerRef.current) {
				listAbortControllerRef.current.abort();
			}
			const controller = new AbortController();
			listAbortControllerRef.current = controller;

			setIsLoading(true);

			const config = { headers: { 'Content-Type': 'application/json' }, signal: controller.signal };

			if (auth.access_token) {
				config.headers.Authorization = `Bearer ${auth.access_token}`;
			}

			let mydict = {};
			for (const [key, value] of Object.entries(filterValues)) {
				if (
					value['type'] === 'utc_date_start' ||
					value['type'] === 'utc_date_end' ||
					value['type'] === 'utc_date'
				) {
					mydict[key] = value['value'].getTime();
				} else if (value['type'] === 'device_ids') {
					// selecting every known machine is equivalent to "no filter" - send it as such so
					// transactions without a machine (e.g. venmo/paypal payouts) aren't dropped by a
					// strict device_ids match on the backend
					const allMachineIds = availableMachines.map((m) => m.value);
					const selected = value['value'];
					const isAllSelected =
						!Array.isArray(selected) ||
						(allMachineIds.length > 0 &&
							selected.length === allMachineIds.length &&
							allMachineIds.every((id) => selected.includes(id)));
					mydict[key] = isAllSelected ? undefined : selected;
				} else {
					mydict[key] = value['value'];
				}
			}

			if (props.show_scrap !== undefined) {
				mydict['show_scrap'] = props.show_scrap;
			}

			axios
				.post(list_get_url, mydict, config)
				.then((results) => {
					setTotal(results.data.length);

					results.data = results.data.map((row, rowIdx) => {
						if (Array.isArray(row.services) && row.services.length) {
							row.services = row.active_services?.map(({ id, name, color }, idx) => {
								return (
									<Chip
										key={`${row.id || rowIdx}-${id}-${idx}`}
										label={name}
										className={`${classes[color]} ${classes.serviceChip}`}
									/>
								);
							});
						}
						return row;
					});

					let processed_data = [];
					processed_data = results.data;

					for (let i in processed_data) {
						for (const [key, value] of Object.entries(processed_data[i])) {
							if (
								key === 'balance' ||
								key === 'donated' ||
								key === 'earned redemption' ||
								key === 'withdrawn'
							) {
								processed_data[i][key] = '$' + processed_data[i][key] / 1e6;
							} else if (key === 'timestamp') {
								processed_data[i][key] = utc_timestamp_to_local_datetime_string(
									processed_data[i][key],
								);
							}
						}
					}

					setEntries(processed_data);
					setIsLoading(false);
				})
				.catch((err) => {
					if (axios.isCancel(err) || err.code === 'ERR_CANCELED') {
						return; // superseded by a newer request, ignore
					}
					// TODO: display if an error happened!
					setIsLoading(false);
				});
		};

		const debounceTimer = setTimeout(fetchListData, 300);

		window.addEventListener('customertable_is_dirty', fetchListData);

		return () => {
			clearTimeout(debounceTimer);
			if (listAbortControllerRef.current) {
				listAbortControllerRef.current.abort();
			}
			window.removeEventListener('customertable_is_dirty', fetchListData);
		};
	}, [filterValues, props.column_names, selectedMachine_ids, props.show_scrap, availableMachines]);

	useEffect(() => {
		const handleResize = () => {
			setScreenSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener('resize', handleResize);

		// Clean up the event listener when the component unmounts
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		if (screenSize.width > 1080) {
			// if a value was in column_names AND is in the optional_columns list, remove it form the first
			let new_column_names = [];
			let new_header_names = [];

			for (let i in props.column_names) {
				if (!props.optional_column_names.includes(props.column_names[i])) {
					new_column_names.push(props.column_names[i]);
					new_header_names.push(props.header_names[i]);
				}
			}

			setColumnNames(new_column_names.concat(props.optional_column_names));
			setHeaderNames(new_header_names.concat(props.optional_header_names));
		} else {
			setColumnNames(props.column_names);
			setHeaderNames(props.header_names);
		}
	}, [screenSize.width, props.column_names]);

	const downloadLedgerEntriesExcel = async () => {
		let mydict = {
			task_ids: entries.map((entry) => entry.task_id),
		};

		for (const [key, value] of Object.entries(filterValues)) {
			if (
				value['type'] === 'utc_date_start' ||
				value['type'] === 'utc_date_end' ||
				value['type'] === 'utc_date'
			) {
				mydict[key] = value['value'].getTime();
			} else {
				mydict[key] = value['value'];
			}
		}

		setIsDownloadingExcel(true);

		try {
			const response = await fetch(
				`${ServerConfig.SERVER_URL}/ledger/get_ledger_entries_as_excel`,
				{
					headers: {
						Accept: 'application/pdf',
						Authorization: `Bearer ${auth.access_token}`,
						'Content-Type': 'application/json',
					},
					method: 'POST',
					body: JSON.stringify(mydict),
				},
			);

			if (!response.ok) {
				throw new Error(`Server responded with status ${response.status}`);
			}

			const blob = await response.blob();
			const fileURL = window.URL.createObjectURL(blob);
			let alink = document.createElement('a');
			alink.href = fileURL;
			alink.download = 'Receipts.xlsx';
			alink.click();
		} catch (err) {
			dispatch({
				type: ALERT_FAIL,
				payload: 'Failed to download ledger entries as Excel. Please try again.',
			});
		} finally {
			setIsDownloadingExcel(false);
		}
	};

	const downloadReceiptPdfs = async () => {
		let mydict = {
			task_ids: entries.map((entry) => entry.task_id),
		};

		setIsDownloadingReceipts(true);

		try {
			const response = await fetch(`${ServerConfig.SERVER_URL}/task/get_receipts_as_pdf`, {
				headers: {
					Accept: 'application/pdf',
					Authorization: `Bearer ${auth.access_token}`,
					'Content-Type': 'application/json',
				},
				method: 'POST',
				body: JSON.stringify(mydict),
			});

			if (!response.ok) {
				throw new Error(`Server responded with status ${response.status}`);
			}

			const blob = await response.blob();
			const fileURL = window.URL.createObjectURL(blob);
			let alink = document.createElement('a');
			alink.href = fileURL;
			alink.download = 'Receipts.pdf';
			alink.click();
		} catch (err) {
			dispatch({
				type: ALERT_FAIL,
				payload: 'Failed to download receipts as PDF. Please try again.',
			});
		} finally {
			setIsDownloadingReceipts(false);
		}
	};

	const onChangeSearchterm = (event) => {
		setSearchterm(event.target.value);
	};

	if (!auth.access_token) {
		return <Redirect to='/login' />;
	}

	function updateFilterValues(datatype, name, value) {
		if (datatype === 'utc_date_start') {
			value.setHours(0, 0, 0, 0);
		} else if (datatype === 'utc_date_end') {
			value.setHours(23, 59, 59, 999);
		}

		console.log('Updating value ' + name + ' (type ' + datatype + ') to value ' + value);

		let copyFoo = { ...filterValues, [name]: { ...filterValues[name], value } };
		setFilterValues(copyFoo); //write it back to state
		props.onFilterChange?.(copyFoo);
	}

	function createFilterInputs() {
		return (
			<Grid container>
				{_.map(props.filter_inputs, (data, key) => {
					if (
						data['type'] === 'utc_date_start' ||
						data['type'] === 'utc_date_end' ||
						data['type'] === 'utc_date'
					) {
						return (
							<Grid xs={12} sm={3} style={{ marginBottom: '20px' }}>
								<Typography variant='h2'>{data['label']}</Typography>
								<DatePicker
									selected={filterValues[data['name']]['value']}
									onChange={(value) => {
										updateFilterValues(data['type'], data['name'], value);
									}}
									dateFormat='MMMM d, yyyy'
									className='form-select'
									popperClassName={classes.datePickerPopper}
								/>
							</Grid>
						);
					} else if (data['type'] === 'materials') {
					} else if (data['type'] === 'device_ids') {
						return (
							<Grid
								xs={12}
								sm={3}
								style={{ 'margin-bottom': '20px', 'padding-right': '20px' }}
							>
								<Typography variant='h2'>{data['label']}</Typography>
								{availableMachines.length > 0 && (
									<Select
										isMulti
										name='machine'
										id='machine'
										onChange={(event) => {
											updateFilterValues(
												data['type'],
												data['name'],
												event.map((e) => e.value),
											);
										}} // [{value:'', label:''}, ...]
										options={availableMachines}
										defaultValue={availableMachines}
										classNamePrefix='react-select'
									/>
								)}
							</Grid>
						);
					} else {
						return <Grid xs={12}>{data['name']}</Grid>;
					}
				})}
			</Grid>
		);
	}

	function createSearchBox() {
		return (
			<>
				<Grid xs={12} md={6} lg={4} style={{ textAlign: 'left', marginTop: '15px' }}>
					<label htmlFor='searchterm' style={{ display: 'inline-block', marginRight: '5px' }}>
						Search
					</label>
					<input
						type='text'
						name='searchterm'
						id='searchterm'
						defaultValue=''
						onChange={onChangeSearchterm}
					/>
				</Grid>
			</>
		);
	}

	function createTransactiontypeSummary() {
		let key_to_summarize = ['transactiontype'];
		let values_to_show = [
			'redemption_buy',
			'cash_payout',
			'paypal_payout',
			'venmo_payout',
			'checkbook_payout',
			'ecocard_payout',
		];
		let summary = {};

		for (let idx in entries) {
			// check if any column of that row matches the searchterm
			let use_entry = false;
			if (searchterm !== '') {
				for (let cidx in column_names) {
					let cn = column_names[cidx];

					let value = String(entries[idx][cn]);
					if (value.toLowerCase().search(searchterm.toLowerCase()) !== -1) {
						// found
						use_entry = true;
						break;
					}
				}
			} else {
				use_entry = true;
			}

			if (use_entry) {
				for (const [key, value] of Object.entries(entries[idx])) {
					if (key_to_summarize.includes(key) && values_to_show.includes(value)) {
						if (!(value in summary)) {
							summary[value] = 0;
						}

						summary[value] =
							summary[value] + entries[idx]['total_transaction_value_microdollars'] / 1e6;
					}
				}
			}
		}

		let USDollar = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

		return (
			<Grid xs={12} md={6} lg={4} style={{ textAlign: 'left', marginTop: '15px' }}>
				Transaction-Summary
				<table>
					<tbody>
						{_.map(summary, (value, key) => {
							return (
								<tr>
									<td style={{ fontSize: '1rem', paddingLeft: '0' }}>{key}</td>
									<td
										style={{
											fontSize: '1rem',
											textAlign: 'right',
											paddingLeft: '15px',
										}}
									>
										{USDollar.format(value.toFixed(2))}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</Grid>
		);
	}

	function handleSortClick(cn) {
		if (sortCol === cn) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortCol(cn);
			setSortDir('asc');
		}
	}

	return (
		<>
			<Grid container style={{ marginBottom: '15px' }}>
				{createFilterInputs()}
				{!isLoading && (
					<>
						<Grid xs={12} lg={4} style={{ textAlign: 'left', marginTop: '15px' }}>
							{total} results
							<br />
							<br />
							{props.show_get_receipt_pdfs && (
								<Button
									style={{ marginRight: '10px' }}
									type='submit'
									simple='true'
									size='large'
									disabled={isDownloadingReceipts}
									onClick={() => downloadReceiptPdfs()}
								>
									{isDownloadingReceipts ? (
										<CircularProgress size={20} />
									) : (
										'Download all Receipts as pdf'
									)}
								</Button>
							)}
							{props.show_get_receipt_pdfs && (
								<Button
									className={classes.downloadButton}
									type='submit'
									simple='true'
									size='large'
									disabled={isDownloadingExcel}
									onClick={() => downloadLedgerEntriesExcel()}
								>
									{isDownloadingExcel ? (
										<CircularProgress size={20} />
									) : (
										'Download Excel'
									)}
								</Button>
							)}
						</Grid>
						{props.show_transactiontype_summary && createTransactiontypeSummary()}
						{createSearchBox()}
					</>
				)}
			</Grid>
			{isLoading && (
				<Grid container spacing={3}>
					<Grid xs={12} style={{ textAlign: 'center' }}>
						<img src={loading_gif} style={{ width: '150px' }} />
					</Grid>
				</Grid>
			)}
			{!isLoading && (
				<div className={classes.table}>
					<table className='table table-striped' style={{ width: '100%' }}>
						<thead>
							<tr className={classes.tableHeader}>
								{_.map(header_names, (hn, i) => {
									const cn = column_names[i];
									const isSortable =
										props.sortable_columns === true ||
										(Array.isArray(props.sortable_columns) &&
											props.sortable_columns.includes(cn));
									const isActive = sortCol === cn;
									return (
										<th
											key={cn}
											style={{
												padding: '10px',
												cursor: isSortable ? 'pointer' : 'default',
												userSelect: 'none',
											}}
											onClick={() => isSortable && handleSortClick(cn)}
										>
											{hn}
											{isSortable && (
												<span
													style={{
														opacity: isActive ? 1 : 0.3,
														fontSize: '0.75em',
														marginLeft: '4px',
													}}
												>
													{isActive && sortDir === 'desc' ? '▼' : '▲'}
												</span>
											)}
										</th>
									);
								})}
								{detail_get_url !== undefined && (
									<th style={{ textAlign: 'right' }}>Edit</th>
								)}
							</tr>
						</thead>

						<tbody>
							{_.map(sortedEntries, (entry, index) => {
								const displayEntry =
									searchterm === '' ||
									column_names.some((cn) =>
										String(entry[cn] ?? '')
											.toLowerCase()
											.includes(searchterm.toLowerCase()),
									);

								if (!displayEntry) {
									return null;
								}

								const isDr6 = column_names.some(
									(cn) => cn.includes('date') && entry[cn]?.includes('(DR6'),
								);

								return (
									<React.Fragment key={entry.id || entry.task_id || index}>
										{isDr6 && (
											<tr
												style={{
													backgroundColor: 'gray',
												}}
											>
												<td>{entry.date}</td>
												<td
													colSpan={column_names.length}
													style={{ textAlign: 'left' }}
												>
													<AssignmentIcon /> {entry.dr6_material}: Shipped CRV
													lbs: {entry.shipped_lbs_crv}, Shipped Scrap lbs:{' '}
													{entry.shipped_lbs_scrap}
												</td>
											</tr>
										)}

										<tr>
											{column_names.map((cn) => {
												let value = entry[cn];

												if (
													cn.includes('microdollar') ||
													cn.includes('balance')
												) {
													const USDollar = new Intl.NumberFormat('en-US', {
														style: 'currency',
														currency: 'USD',
													});

													value = USDollar.format((value / 1e6).toFixed(2));
												}

												return (
													<td key={cn} style={{ padding: '10px' }}>
														{value}
													</td>
												);
											})}

											{detail_get_url && (
												<EditComponent
													detail_get_url={detail_get_url}
													detail_put_url={detail_put_url}
													detail_delete_url={detail_delete_url}
													rowData={entry}
													editable_fields={editable_fields}
													non_editable_fields={non_editable_fields}
													ledger_get_url={ledger_get_url}
													ledger_insert_url={ledger_insert_url}
													ledger_allow_entries={ledger_allow_entries}
													show_machine_stats={show_machine_stats}
												/>
											)}
										</tr>
									</React.Fragment>
								);
							})}
						</tbody>
					</table>

					{add_new_url !== undefined && (
						<AddComponent add_new_url={add_new_url} editable_fields={editable_fields} />
					)}
				</div>
			)}{' '}
			{/* not isLoading */}
		</>
	);
};

export default NonPaginatedList